import type { ComputedRef, Ref } from "vue";
import type { WebsitePageQuery } from "~/graphql/generated";

/** Block shown at the top of a `WebsitePage` */
type IntroductionContentBlock = string;

/** Block shown for brochures that are active this week, or similar. */
type TimedContentBlock = string;

/** Block shown near brochures that are related to current `websitePage`. */
type SimilarContentBlock = string;

/** Block shown for offers. */
type OffersContentBlock = string;

/** The general rich-text `content` field, used instead, alongside `contentBlocks`. */
type GeneralContent = string;

/** Represents an approximation of what the `websitePage.contentBlocks` represents.
 * Each block is a HTML string, that gets used in the `DynamicContent` component. But since it's just an array of strings in the API, there is no way to properly validate this data.
 */
export type DynamicPage = {
  introductionBlock?: IntroductionContentBlock;
  similarBlock?: SimilarContentBlock;
  timedBlock?: TimedContentBlock;
  offersBlock?: OffersContentBlock;
  content?: GeneralContent;
};

/* Parses a `websitePage` into something more structured so it can be used on several other pages. */
export function useDynamicContent(
  page: Ref<WebsitePageQuery | undefined> | null,
): ComputedRef<DynamicPage | null> {
  const result = computed((): DynamicPage | null => {
    const websitePage = page?.value?.websitePage;

    if (!websitePage || !("contentBlocks" in websitePage)) {
      return null;
    }

    /** Look at this fancy array destructuring! */
    const [introductionBlock, similarBlock, timedBlock, offersBlock] =
      websitePage.contentBlocks;

    return {
      introductionBlock,
      similarBlock,
      timedBlock,
      offersBlock,
      content: websitePage.content ?? undefined,
    };
  });

  return result;
}
